<script setup lang="ts">
  import {
    AppVersion,
    AppVersionTone,
    FontIcon,
    Tooltip,
    TundrLogo,
    TundrLogoVariant,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { useWindowSize } from '@vueuse/core';
  import { computed, inject, provide, watch, watchEffect } from 'vue';
  import { SIDEBAR_COLLAPSED_KEY } from '../../constants';
  import { useSidebarStore } from '../../store/sidebar.store';
  import {
    appVersionClass,
    closedSidebarClass,
    collapseButtonClass,
    collapseIconClass,
    collapseSidebarHeaderClass,
    collapseWrapperCollapsedClass,
    dividerClass,
    dividerVariants,
    openSidebarClass,
    poweredByLogoClass,
    poweredByLogoCollapsedClass,
    sidebarFooterClass,
    sidebarForegroundVariants,
    sidebarHeaderClass,
    sidebarLogoClass,
    sidebarSurfaceVariants,
    sidebarWrapperClass,
    appVersionValueClass,
    updateButtonClass,
    updateDotClass,
  } from './Sidebar.css';
  import { SidebarVariant, type SidebarProps } from './Sidebar.types';

  const MOBILE_BREAKPOINT = 1024;

  const props = withDefaults(defineProps<SidebarProps>(), {
    collapsible: true,
    variant: SidebarVariant.Light,
    updateAvailable: false,
  });

  const emit = defineEmits<{ updateClick: [] }>();

  const appVersion = inject<string>('appVersion');

  const { t } = useLocale('sidebar');

  const sidebarStore = useSidebarStore();
  const isCollapsible = computed(() => props.collapsible);
  const collapsed = computed(
    () => isCollapsible.value && sidebarStore.collapsed,
  );

  provide(SIDEBAR_COLLAPSED_KEY, collapsed);

  const isOnBrand = computed(() => props.variant === SidebarVariant.OnBrand);

  watchEffect(() => {
    sidebarStore.setVariant(props.variant);
  });

  const { width: windowWidth } = useWindowSize();

  watch(
    () => windowWidth.value < MOBILE_BREAKPOINT,
    (isMobile, wasMobile) => {
      if (isCollapsible.value && isMobile && !wasMobile) {
        sidebarStore.setCollapsed(true);
      }
    },
    { immediate: true },
  );

  const poweredByLabel = computed(
    () => props.poweredByLabel ?? t('powered_by'),
  );

  const collapseButtonAriaLabel = computed(() =>
    collapsed.value
      ? (props.expandLabel ?? t('expand_aria_label'))
      : (props.collapseLabel ?? t('collapse_aria_label')),
  );

  const sidebarClasses = computed(() => [
    sidebarWrapperClass,
    collapsed.value ? closedSidebarClass : openSidebarClass,
    sidebarSurfaceVariants[props.variant],
  ]);

  const sidebarFooterClasses = computed(() => [
    sidebarFooterClass,
    { [collapseWrapperCollapsedClass]: collapsed.value },
    sidebarSurfaceVariants[props.variant],
  ]);

  const sidebarHeaderClasses = computed(() => [
    sidebarHeaderClass,
    { [collapseSidebarHeaderClass]: collapsed.value },
  ]);

  const dividerClasses = computed(() => [
    dividerClass,
    dividerVariants[props.variant],
  ]);

  const sidebarLogoClasses = computed(() => [
    sidebarLogoClass,
    sidebarForegroundVariants[props.variant],
  ]);

  const collapseIconClasses = computed(() => [
    collapseIconClass,
    sidebarForegroundVariants[props.variant],
  ]);

  const poweredByLogoClasses = computed(() => [
    poweredByLogoClass,
    { [poweredByLogoCollapsedClass]: collapsed.value },
  ]);

  const appVersionTone = computed(() =>
    isOnBrand.value ? AppVersionTone.OnBrand : AppVersionTone.Default,
  );
</script>

<template>
  <div :class="sidebarClasses">
    <div :class="sidebarHeaderClasses">
      <slot name="header" v-bind="{ collapsed }" />
    </div>
    <div v-if="$slots.header" :class="dividerClasses" />
    <slot name="top-cta" />
    <slot name="body" />
    <div :class="sidebarFooterClasses">
      <div :class="sidebarLogoClasses">
        <span v-if="!collapsed">{{ poweredByLabel }}</span>
        <span v-if="isOnBrand" :class="poweredByLogoClasses">
          <TundrLogo
            :variant="
              collapsed ? TundrLogoVariant.Small : TundrLogoVariant.Light
            "
          />
        </span>
        <TundrLogo
          v-else
          :variant="
            collapsed ? TundrLogoVariant.SmallDark : TundrLogoVariant.Dark
          "
        />
      </div>
      <button
        v-if="isCollapsible"
        type="button"
        data-testid="collapse-button"
        :class="collapseButtonClass"
        :aria-label="collapseButtonAriaLabel"
        @mousedown.prevent
        @click="sidebarStore.toggleCollapse"
      >
        <FontIcon
          :class="collapseIconClasses"
          :icon-name="
            collapsed ? TUNDR_ICONS.SLIDE_RIGHT : TUNDR_ICONS.SLIDE_LEFT
          "
        />
      </button>
    </div>
    <div v-if="appVersion || updateAvailable" :class="appVersionClass">
      <div v-if="appVersion" :class="appVersionValueClass">
        <AppVersion :collapsed="collapsed" :tone="appVersionTone" />
      </div>
      <Tooltip
        v-if="updateAvailable"
        :title="t('update_available_title')"
        :description="t('update_available_description')"
      >
        <button
          type="button"
          data-testid="update-available-button"
          :class="updateButtonClass"
          :aria-label="t('update_available_title')"
          @click="emit('updateClick')"
        >
          <span :class="updateDotClass" />
        </button>
      </Tooltip>
    </div>
  </div>
</template>
